import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Chip,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import { partnerService, Partner } from "../../../apis/partners";
import { offerTypeService, OfferType } from "../../../apis/offerTypes";
import axios from "axios";

interface PartnerSettingsProps {
  partnerId: string;
}

const PartnerSettings: React.FC<PartnerSettingsProps> = ({ partnerId }) => {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [offerTypes, setOfferTypes] = useState<OfferType[]>([]);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingRegistration, setLoadingRegistration] = useState(false);
  const [loadingCompanyName, setLoadingCompanyName] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  useEffect(() => {
    fetchPartnerDetails();
    fetchOfferTypes();
  }, [partnerId]);

  const fetchOfferTypes = async () => {
    try {
      const response = await offerTypeService.getActiveOfferTypes();
      if (response.status === "success" || response.status === true) {
        setOfferTypes(response.data.offer_types || []);
      }
    } catch (error) {
      console.error("Failed to fetch offer types:", error);
    }
  };

  const getOfferTypeName = (offerTypeId: string) => {
    const offerType = offerTypes.find((ot) => ot._id === offerTypeId);
    return offerType ? offerType.name : offerTypeId;
  };

  const fetchPartnerDetails = async () => {
    try {
      const data = await partnerService.getPartnerById(partnerId);
      setPartner(data);
      setRegistrationNumber(data.registrationNumber || "");
      setCompanyName(data.companyName || "");
    } catch (error) {
      console.error("Failed to fetch partner details:", error);
    }
  };

  const handleUpdateRegistrationNumber = async () => {
    setLoadingRegistration(true);
    try {
      await partnerService.updatePartner(partnerId, { registrationNumber });
      setSnackbar({
        open: true,
        message: "Registration number updated successfully",
        severity: "success",
      });
      // Refresh partner details
      await fetchPartnerDetails();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update registration number",
        severity: "error",
      });
    } finally {
      setLoadingRegistration(false);
    }
  };

  const handleUpdateCompanyName = async () => {
    setLoadingCompanyName(true);
    try {
      await partnerService.updatePartner(partnerId, { companyName });
      setSnackbar({
        open: true,
        message: "Company name updated successfully",
        severity: "success",
      });
      // Refresh partner details
      await fetchPartnerDetails();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update company name",
        severity: "error",
      });
    } finally {
      setLoadingCompanyName(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setSnackbar({
        open: true,
        message: "Please fill in all password fields",
        severity: "error",
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbar({
        open: true,
        message: "New password and confirm password do not match",
        severity: "error",
      });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: "Password must be at least 6 characters long",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      // Update password API call
      await axios.put(
        `${process.env.REACT_APP_AUTH_SERVICE_BASE_URL}/auth/v1/users/partners/${partnerId}`,
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setSnackbar({
        open: true,
        message: "Password updated successfully",
        severity: "success",
      });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to update password",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Account Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Partner Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Partner Information
            </Typography>
            {partner && (
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Partner Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {partner.firstName} {partner.lastName}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {partner.email}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  Phone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {partner.phone}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  Offer Type
                </Typography>
                <Chip
                  label={getOfferTypeName(partner.offerTypeId)}
                  color="primary"
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="textSecondary" display="block">
                  Offer type can only be changed by Admin
                </Typography>

                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Agency Code
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {partner.agencyCode}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Registration Number
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
                  <TextField
                    value={registrationNumber}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                    placeholder="Enter registration number"
                    size="small"
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleUpdateRegistrationNumber}
                    disabled={loadingRegistration || registrationNumber === (partner.registrationNumber || "")}
                    size="small"
                  >
                    {loadingRegistration ? "Updating..." : "Update"}
                  </Button>
                </Box>

                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  Company Name
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter company name"
                    size="small"
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleUpdateCompanyName}
                    disabled={loadingCompanyName || companyName === (partner.companyName || "")}
                    size="small"
                  >
                    {loadingCompanyName ? "Updating..." : "Update"}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Change Password */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Change Password
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Current Password"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                fullWidth
              />
              <TextField
                label="New Password"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                fullWidth
              />
              <TextField
                label="Confirm New Password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleChangePassword}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? "Updating..." : "Update Password"}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerSettings;

